import { ulid } from '@delivery/shared';

/**
 * 接口基址。
 *
 * ★ 浏览器里走相对路径 `/api/v1` —— 由 vite / 反向代理转给后端,
 *   不跨域,也不用管部署在哪。
 *
 * ★ **打包成 App 之后没有代理**:页面是从本地文件加载的
 *   (iOS 上是 `capacitor://localhost`),相对路径会打到 App 自己身上,
 *   表现是每个接口都 404 而且看不出为什么。
 *   所以打包时用 `VITE_API_BASE` 写一个绝对地址,
 *   例如 `http://192.168.88.9:3000/api/v1`。
 */
const BASE =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') || '/api/v1';

/*
 * ★ 把自己在用的接口地址留给**浏览器打印台**(`public/station.html`)。
 *
 *   打印台是一个独立的静态页,读不到 `import.meta.env`,只能自己从网址猜。
 *   猜法再好也有猜不准的时候:管理端用 `VITE_API_BASE` 指向了另一台后端,
 *   而打印台按同源猜 —— 两边连的是**不同的库**,于是手机上按了打印,
 *   打印台那头一直「等着」,什么都不出。这种错没有任何报错指向它。
 *
 *   同源的 localStorage 是两边唯一天然共享的东西。写成绝对地址,
 *   因为打印台可能是从别的路径打开的。
 *
 *   这是**线索**不是命令:打印台配对成功之后会把当时用的地址钉死
 *   (钥匙是那台发的),这条只在它还没配对时起作用。
 */
try {
  localStorage.setItem('station.apiHint', BASE.startsWith('http') ? BASE : location.origin + BASE);
} catch {
  // 隐私模式下写不了 —— 打印台会退回自己猜,不该因此让管理端起不来
}

const TOKEN_KEY = 'delivery.admin.tokens';
const DEVICE_KEY = 'delivery.admin.device';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

/** 业务异常。司机端按 code 决定怎么处理,按 message 显示给司机 */
/** 把校验失败的字段原因拼进那句话 —— 只显示「请求参数校验失败」等于没报错 */
function describe(error: ApiError): string {
  const fields = (
    error.details as { fields?: Array<{ path?: string; message?: string }> } | undefined
  )?.fields;
  if (!Array.isArray(fields) || fields.length === 0) return error.message;
  const parts = fields
    .slice(0, 3)
    .map((f) => [f.path, f.message].filter(Boolean).join(' '))
    .filter(Boolean);
  return parts.length > 0 ? `${error.message}:${parts.join(';')}` : error.message;
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    /*
     * ★ 校验失败要把**哪个字段、为什么**也带进 message。
     *
     *   服务端给的 `message` 是一句通用话:「请求参数校验失败」——
     *   而真正有用的东西在 `details.fields` 里
     *   (例如 `orderIds: Array must contain at most 200 element(s)`)。
     *   界面上到处都是 `e.message`,只显示那句通用话的话,
     *   人看到的是一个无从下手的报错。
     *
     *   真事:批量取消 200 单以上,界面只说「请求参数校验失败」,
     *   而原因是「一次最多 200 单」—— 那句话就在 details 里躺着。
     */
    super(describe(error));
    this.name = 'ApiFailure';
  }

  get code(): string {
    return this.error.code;
  }

  /**
   * 服务端随错误一起给的结构化信息。
   *
   * ★ 有了它,界面才能按**标记**决定下一步(比如「要不要弹那张确认卡」),
   *   而不是去匹配错误文案 —— 那句话改一个字,分支就静静失效了。
   */
  get details(): Record<string, unknown> | undefined {
    return this.error.details;
  }
}

export const tokens = {
  get(): Tokens | null {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  },
  set(t: Tokens): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(t));
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  },
};

/** 设备 ID 持久化 —— 一账号一设备,换设备会踢掉旧会话 */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = ulid();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  // 并发请求同时 401 时只刷新一次,避免刷新风暴
  if (refreshing) return refreshing;
  const current = tokens.get();
  if (!current?.refreshToken) return false;

  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!res.ok) return false;
      tokens.set((await res.json()) as Tokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** 401 时不尝试刷新(登录接口本身) */
  noRetry?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const doFetch = async (): Promise<Response> => {
    const t = tokens.get();
    return fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers: {
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(t ? { Authorization: `Bearer ${t.accessToken}` } : {}),
      },
      ...(opts.body ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  };

  let res = await doFetch();

  // access token 过期时静默刷新重试,司机不该被打断
  if (res.status === 401 && !opts.noRetry) {
    const body = (await res
      .clone()
      .json()
      .catch(() => null)) as { error?: ApiError } | null;
    if (body?.error?.code === 'TOKEN_EXPIRED' && (await tryRefresh())) {
      res = await doFetch();
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: ApiError } | null;
    throw new ApiFailure(
      res.status,
      body?.error ?? { code: 'NETWORK_ERROR', message: '网络异常,请稍后重试' },
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * 带文件的请求(multipart)。
 *
 * ★ 不能走 `api()` —— 它会设 `Content-Type: application/json`,
 *   那样 multipart 的 boundary 就丢了,服务端收到的是一团解不开的字节。
 *
 * ★ 单独写这一个而不是在每处 `fetch('/api/v1/...')`:
 *   写死 `/api/v1` 会绕开 `VITE_API_BASE` —— 打包成 App 之后
 *   相对路径打到 App 自己身上,表现是「网页能传、App 传不了」。
 *   (ImportDialog 里那处还是写死的,以后一起收。)
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const t = tokens.get();
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: t ? { Authorization: `Bearer ${t.accessToken}` } : {},
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: ApiError } | null;
    throw new ApiFailure(
      res.status,
      body?.error ?? { code: 'NETWORK_ERROR', message: '网络异常,请稍后重试' },
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * 下载文件。
 *
 * ★ 不能用 `<a href>` 或 `window.open` —— 那样发不出 Authorization 头,
 *   服务器会返回 401。必须 fetch 拿到 blob 再触发下载。
 *   (把 token 塞进 query string 是另一种绕法,但那会把凭证写进
 *    浏览器历史、代理日志和 Referer,不能这么干)
 */
export async function download(path: string): Promise<void> {
  const t = tokens.get();
  const res = await fetch(`${BASE}${path}`, {
    headers: t ? { Authorization: `Bearer ${t.accessToken}` } : {},
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: ApiError } | null;
    throw new ApiFailure(res.status, body?.error ?? { code: 'NETWORK_ERROR', message: '下载失败' });
  }

  // 文件名以服务器给的为准 —— 里面带了时间戳
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'download.csv';

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 立刻 revoke 会让部分浏览器来不及取,延后释放
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * 给这台设备起个人认得出的名字。
 *
 * ★ 设备列表里只有一串随机 id 的话,「这台是不是我」没人答得上来 ——
 *   于是那一屏形同虚设:看到可疑设备也不敢踢。
 *   粗略认一下机型和浏览器就够用了,不做指纹。
 */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : '未知设备';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : '浏览器';
  return `${os} · ${browser}`;
}
